import { ANSWER_FACET } from './answer-ref.js';
import { matchedKeywordOption } from './keyword.js';
/**
 * HOW A BLOCK'S ANSWER SPACE PARTITIONS, for a later block whose versions it drives.
 *
 * A numeric recall can already shape a later question through equations — an answer key reads
 * `2*[earlier]` and the question needs no variants at all. A LITERAL answer has no such path,
 * and this is it: the later block carries one version per thing the earlier one could have been
 * answered with, and the student's own answer picks which applies.
 *
 * ## Not the same question as `answerChoicesFor`
 *
 * That one asks "what values can I test this against", and its answers overlap heavily with
 * these. It is a different question all the same — it offers a WRONG value and a near miss,
 * which are samples rather than cells, and it offers them for blocks whose answer is a number
 * on a line, which cannot be partitioned at all. Deriving one from the other would tie a
 * teacher's version axis to what the test bench happens to offer.
 *
 * ## The criterion, which is not "exhaustively enumerable"
 *
 * It is "partitionable into a few named buckets, with a catch-all where the space is open".
 * The catch-all is what MAKES an open space exhaustive, which is why Keyword qualifies: a
 * student may type anything, and "anything else" is a real bucket a teacher can author for.
 *
 * Two rows are therefore not the same thing and both are needed:
 *
 *   - **catch-all** — answered, but not with anything the teacher enumerated. Only where the
 *     space is open, because a closed one has nothing outside it to catch.
 *   - **blank** — not answered at all. Universal: any source can go unanswered, by a student
 *     who skipped it or by flow that never routed through it.
 *
 * Blank is also what retires the hazard in `versions.ts`'s claim that "every version is
 * reachable by construction". That holds for a drawn axis and not for a driven one — but with
 * a blank row the axis is exhaustive again, so nothing ever needs a fallback to row one.
 */
/** Reserved row ids. `__other` matches the open class `answerChoicesFor` already uses. */
/**
 * WHICH ANSWER THIS IS, spelled once.
 *
 * Two things need the same string and reached it by different routes: a recall row's `source`,
 * which says which answer the row is about, and an alias union row's `sourceOptions`, which say
 * which member options it is made of. They have to agree exactly or nothing can be matched
 * across them — and they did not, which is how the cycle on an alias driver came to offer one
 * position. `facet || value` is the half that is easy to get wrong: an absent facet reads as
 * `value` on one side and as empty on the other.
 */
export const answerSourceKey = (taskId, blockId, facet) => `${taskId}:${blockId}:${facet || ANSWER_FACET.value}`;
/** One option under that answer — an entry in `DrivenRow.sourceOptions`. */
export const sourceOptionKey = (source, optionId) => `${source}:${optionId}`;
export const DRIVEN_ROW = { other: '__other', blank: '__blank' };
/**
 * THE ANSWER A STUDENT WHO ANSWERED NOTHING LEAVES — the stand-in that selects the blank row.
 *
 * The blank row has a version of its own and was authorable from nowhere: the editor's pager
 * and the card's cycle were both built from the driver's answer CHOICES, and "no answer" is
 * the absence of one, so an MCQ with three options projected four versions and both controls
 * offered three. Reported as "why am i not seeing No Answer in the sequence?".
 *
 * NOT `null`, which is what the bench reads as "clear my pick" — a stand-in set to null falls
 * straight back to the sampled correct answer, which is the first row again. An EMPTY ANSWER
 * is a different statement from no stand-in at all, and only the empty one can be chosen.
 *
 * One value for every driver type rather than one per type: `drivenRowIdFor` reads an answer
 * with no selection, no text and no number as blank on all six of its branches, which is the
 * same reading as a student who submitted an untouched block. Pinned across the types in
 * `driven-rows.test.ts`, so a seventh branch that disagrees fails there rather than silently
 * stranding its blank row.
 */
export const BLANK_DRIVEN_ANSWER = {};
const text = (v) => String(v ?? '').replace(/<[^>]*>/g, '').trim();
/** Options as `{ id, text }`, for the three block shapes that store them that way. */
function optionRows(data) {
    return (Array.isArray(data.options) ? data.options : [])
        .map(o => o)
        /* A MEDIA option survives a blank label: its picture is the answer, and `text` is only the
           internal name the editor relabels "Label". Filtering on text alone dropped exactly the
           rows a teacher most needs to see. */
        .filter(o => text(o.text) !== '' || o.media)
        .map(o => ({
        id: String(o.id ?? ''),
        label: text(o.text),
        kind: 'option',
        html: typeof o.text === 'string' ? o.text : undefined,
        media: o.media,
    }));
}
/**
 * The rows a block's answer space gives, or null when it has no partition worth authoring.
 *
 * `facet` names the blank for FITB, whose answer space is per blank — which is also why FITB
 * does not explode into a product: the facet has already narrowed it to one.
 */
export function drivenRowsFor(blockType, data, facet) {
    const blank = { id: DRIVEN_ROW.blank, label: '', kind: 'blank' };
    const other = { id: DRIVEN_ROW.other, label: '', kind: 'other' };
    switch (blockType) {
        /*
         * CLOSED SPACES: every answer is one of the options, so there is nothing for a catch-all
         * to catch. A student can still fail to answer, which is what the blank row is for.
         */
        case 'mcq': {
            /*
             * SINGLE-SELECT ONLY, and this is a limit rather than something to work around. The
             * space enumerates at 2^N, but no SELECTOR is honest: "the first selected" and "the one
             * most selected" both silently pick a version the teacher never reasoned about, which is
             * the quiet wrong answer this codebase keeps finding. A teacher who wants this can ask
             * the question as a single-select.
             */
            if (data.allowMultiple === true)
                return null;
            const rows = optionRows(data);
            return rows.length ? [...rows, blank] : null;
        }
        case 'dropdown': {
            const rows = optionRows(data);
            return rows.length ? [...rows, blank] : null;
        }
        case 'scale': {
            /*
             * A point's position is its identity — `applyScaleVersion` keys roles the same way,
             * because a point on an axis has a position rather than an id. The label names the row
             * where there is one, which is the whole reason a scale is worth driving from: five
             * versions called 1 to 5 say nothing a teacher can check.
             */
            const rows = (Array.isArray(data.values) ? data.values : [])
                .map(v => v)
                .filter(v => v.value !== undefined)
                .map(v => ({
                id: String(v.value),
                label: text(v.label) || String(v.value),
                kind: 'option',
                html: typeof v.label === 'string' && v.label !== '' ? v.label : String(v.value),
            }));
            return rows.length ? [...rows, blank] : null;
        }
        /*
         * OPEN SPACES: the enumerated words are what the teacher thought of, not what a student
         * may type. The catch-all is what makes the partition exhaustive.
         */
        case 'keyword': {
            const rows = optionRows(data);
            return rows.length ? [...rows, other, blank] : null;
        }
        case 'fitb': {
            const blanks = Array.isArray(data.blanks) ? data.blanks : [];
            const target = blanks
                .map(b => b)
                .find(b => String(b.id ?? '') === facet);
            // Without a blank named, there is no single space to partition — the block's answer is
            // one per blank, and a product of them is the explosion the facet exists to avoid.
            if (!target || !Array.isArray(target.words))
                return null;
            const rows = target.words
                .map(w => w)
                .filter(w => text(w.text) !== '')
                .map(w => ({
                id: String(w.id ?? ''),
                label: text(w.text),
                kind: 'option',
                html: typeof w.text === 'string' ? w.text : undefined,
            }));
            return rows.length ? [...rows, other, blank] : null;
        }
        /*
         * EVERYTHING ELSE HAS NO PARTITION WORTH AUTHORING. Ordering and Sort answer with a
         * STRUCTURE — a permutation, an assignment of items to bins — where a catch-all over `N!`
         * is a catch-all over everything. The numeric blocks are continuous, which is what
         * equations are for, and a reference to one can already shape a later answer without any
         * of this.
         */
        default:
            return null;
    }
}
/**
 * AN ANSWER THAT LANDS ON A GIVEN ROW — the inverse of `drivenRowIdFor`.
 *
 * The bench needs it because an ALIAS merges options from members on different pathways, and a
 * stand-in was only ever built from the alias's FIRST live member. A union row contributed
 * solely by the second member could not be selected at all: a two-site alias projected six
 * versions and the editor could open four. Reported as "i only see options for A and B ... i
 * went and updated pathway 2 to a and c, and no c appears for setup" — the C row was in the
 * bank the whole time, merged correctly, and unreachable.
 *
 * Built from the ROW rather than from `answerChoicesFor`, which is what made this possible at
 * all: a choice's sample carries the RESOLVED option text (chips substituted) while
 * `drivenRowIdFor` matches against the stored one, and `aliasDriverSpace` has no variable
 * values to resolve with. `row.html` is the stored text, so the round trip is exact.
 *
 * Null where no answer reaches the row: a catch-all needs a word the teacher has not written,
 * and inventing one is not a stand-in. `__blank` is deliberately not special-cased here — the
 * caller holds `BLANK_DRIVEN_ANSWER` for that, which is type-independent.
 *
 * Pinned by round trip in `driven-rows.test.ts`: every row of every drivable type must come
 * back from `drivenRowIdFor` as itself. A type added to `drivenRowsFor` without a case here
 * fails there rather than silently stranding half an alias.
 */
export function answerForDrivenRow(blockType, row, facet) {
    if (row.kind !== 'option')
        return null;
    switch (blockType) {
        /* Both record what the student SAW, which is what `optionIdForText` matches on when an
           answer carries no id — and a stand-in never carries one. */
        case 'mcq':
        case 'dropdown':
            return row.html === undefined ? null : { selectedTexts: [row.html] };
        /* A point's position is its identity, and the row id IS that position. */
        case 'scale': {
            const n = Number(row.id);
            return Number.isFinite(n) ? n : null;
        }
        /* Matched through the grader's own matcher, so the word that selects a row here is a word
           that would really grade onto it. The stored text matches itself by construction. */
        case 'keyword':
            return row.html ?? null;
        /* Keyed by blank id at the top level — the shape the block reports, and the shape
           `drivenRowIdFor` reads. Without a facet there is no blank to answer. */
        case 'fitb':
            return facet && row.html !== undefined ? { [facet]: row.html } : null;
        default:
            return null;
    }
}
/** Whether this block can drive another's versions at all — the menu's question. */
export function canDriveVersions(blockType, data, facet) {
    return drivenRowsFor(blockType, data, facet) !== null;
}
/** The types that can never partition, whatever is authored in them. */
const PERMANENT = {
    numerical: 'range',
    slider: 'range',
    gps: 'measurement',
    compass: 'measurement',
    tilt: 'measurement',
    rotation: 'measurement',
    ordering: 'order',
    sort: 'grouping',
};
/** What an EMPTY block of each drivable type is missing, in that type's own words. */
const WHEN_EMPTY = {
    mcq: 'noOptions',
    dropdown: 'noOptions',
    scale: 'noPoints',
    keyword: 'noWords',
    fitb: 'noBlankWords',
};
export function driverRefusal(blockType, data, facet) {
    /* Multi-select is a property of the DATA and permanent all the same — the teacher can turn
       it off, but no amount of authoring makes a combination partition while it is on. */
    if (blockType === 'mcq' && data.allowMultiple === true)
        return 'combination';
    const permanent = PERMANENT[blockType];
    if (permanent)
        return permanent;
    if (drivenRowsFor(blockType, data, facet))
        return null;
    /* A type with no entry in either table is one nobody has classified: refuse it, rather than
       offer a row that cannot work, and let the agreement test name it on the day it is added. */
    return WHEN_EMPTY[blockType] ?? 'noOptions';
}
export function driverSpacesFor(blockType, data) {
    if (blockType !== 'fitb') {
        const refusal = driverRefusal(blockType, data);
        return [{ index: 0, eligible: refusal === null, refusal }];
    }
    const blanks = (Array.isArray(data.blanks) ? data.blanks : [])
        .map(b => b);
    return blanks.map((b, index) => {
        /* ONE call, not two: `eligible` derived from the refusal rather than asked separately, or
           a blank could be greyed for one reason and explained by another. */
        const refusal = driverRefusal(blockType, data, String(b.id ?? ''));
        return { facet: String(b.id ?? ''), index, eligible: refusal === null, refusal };
    });
}
/**
 * THE OPTION A TEXT-SHAPED ANSWER NAMES — because a STAND-IN records texts, not ids.
 *
 * A student's submission carries `selectedIds` (MCQ) or `selectedId` (Dropdown); the bench's
 * stand-ins come from `answerChoicesFor`, which records `{ selectedTexts: [...] }` so that a
 * chip in prose can print the words without a second lookup. Both are answers to the same
 * question and this axis has to place either, or the two commonest driver types silently
 * resolve every stand-in to Blank — which is what made clicking an earlier answer change the
 * sentence and not the question it governs.
 *
 * Exact text, not a normalised match: both sides are the option's own stored string, so
 * anything looser would be matching two spellings of one option that cannot differ.
 */
function optionIdForText(data, texts) {
    const first = Array.isArray(texts) ? texts[0] : texts;
    if (typeof first !== 'string')
        return null;
    const options = Array.isArray(data.options) ? data.options : [];
    for (const raw of options) {
        const o = raw;
        if (typeof o.text === 'string' && o.text === first)
            return String(o.id ?? '');
    }
    return null;
}
/**
 * WHICH ROW AN ANSWER LANDS IN — the selector, and the other half of the partition above.
 *
 * Every path ends on a row, which is the property that makes the axis safe: `pickVersion`'s
 * fallback to version one exists for a drawn axis and would silently grade an unanswered
 * student against the first cell here. With a blank row there is nothing to fall back FROM.
 *
 * ## It reads ids, where a chip reads text
 *
 * `resolveAnswerRef` shows an MCQ answer as `selectedTexts` — what the student SAW, captured
 * at submission because an option's text may hold a variable chip and two students saw
 * different words. That is right for a sentence quoting them and wrong here: a row is keyed to
 * an option's IDENTITY, which survives a re-roll and a re-word. Both are recorded, so each
 * question reads the field that answers it.
 *
 * ## And it matches words with the grader's own matcher
 *
 * Keyword and FITB go through `matchedKeywordOption`, which is what `evaluateKeyword` and
 * `evaluateFitb` grade with. A second implementation would differ on case or accents, and the
 * version shown would disagree with the feedback fired about one typed word.
 */
export function drivenRowIdFor(blockType, data, answer, facet) {
    const rows = drivenRowsFor(blockType, data, facet);
    if (!rows)
        return null;
    const has = (id) => rows.some(r => r.id === id);
    /*
     * AN ANSWER WE CANNOT PLACE IS BLANK, not a guess. In a closed space this means the option
     * was deleted after the answer was given, which a pinned edition prevents within a session —
     * so it is unreachable in practice and must still not resolve to "whichever row is first".
     */
    const placed = (id) => id && has(id) ? id : DRIVEN_ROW.blank;
    if (answer === null || answer === undefined)
        return DRIVEN_ROW.blank;
    const a = (typeof answer === 'object' ? answer : {});
    switch (blockType) {
        case 'mcq': {
            // Single-select by construction — `drivenRowsFor` refuses a multi-select source.
            const ids = Array.isArray(a.selectedIds) ? a.selectedIds : [];
            if (ids.length > 0)
                return placed(String(ids[0]));
            return placed(optionIdForText(data, a.selectedTexts));
        }
        case 'dropdown': {
            if (a.selectedId !== undefined && a.selectedId !== null)
                return placed(String(a.selectedId));
            return placed(optionIdForText(data, a.selectedTexts));
        }
        case 'scale': {
            // A point's position is its identity, so the submitted ordinal IS the row id.
            const n = typeof answer === 'number' ? answer : Number(answer);
            return Number.isFinite(n) ? placed(String(n)) : DRIVEN_ROW.blank;
        }
        case 'keyword': {
            const typed = typeof answer === 'string' ? answer : '';
            if (typed.trim() === '')
                return DRIVEN_ROW.blank;
            const matched = matchedKeywordOption(typed, (Array.isArray(data.options) ? data.options : []), data.caseSensitive === true, data.accentSensitive === true);
            // Answered, with something nobody listed — which is the catch-all, never blank.
            return matched ? placed(matched.id) : DRIVEN_ROW.other;
        }
        case 'fitb': {
            /*
             * KEYED BY BLANK ID AT THE TOP LEVEL, which is what the block reports and what
             * `resolveAnswerRef` reads — `useReportAnswer(submittedValues)`, a flat
             * `Record<blankId, string>`.
             *
             * This read `a.values[blankId]` until a browser run caught it: a student typed the
             * correct word and the axis landed on the Blank row, because the wrapper does not
             * exist. The unit test agreed with the bug, because I wrote it from the same wrong
             * assumption rather than from the recorded answer — which is why `recall-agreement`
             * below now asks both readers about one object instead.
             */
            const typed = typeof a[facet ?? ''] === 'string' ? String(a[facet ?? '']) : '';
            if (typed.trim() === '')
                return DRIVEN_ROW.blank;
            const blank = (Array.isArray(data.blanks) ? data.blanks : [])
                .map(b => b)
                .find(b => String(b.id ?? '') === facet);
            const words = (Array.isArray(blank?.words) ? blank.words : []);
            const matched = matchedKeywordOption(typed, words, data.caseSensitive === true, data.accentSensitive === true);
            return matched ? placed(matched.id) : DRIVEN_ROW.other;
        }
        default:
            return null;
    }
}
//# sourceMappingURL=driven-rows.js.map